// Chapter registry for the docs. Source of truth: src/docs/*.md (hand-edited).
// Docs synced to rayfish@a2ca33cf (0.5.8 source + unreleased, 2026-10-12).
// Mesh SSH commands/ports, self-SSH, apply membership and target exclusions,
// signed destruction, paired-network sync, app updates, file trust/retries,
// windowed mDNS, forwarding engines, diagnostics, and saved network names.
// Previous sync (rayfish@f5dd60e..ee7f267):
// Managed SSH grants and firewall exclusions in ray apply, partial DNS,
// opt-in status IPs/machines, live mDNS, FreeBSD DNS, desktop firewall controls,
// macOS SSH helper and logs, selective unpair, and join-queue eviction metrics.
// Previous sync (rayfish@7ef6b2de..f5dd60e):
// Intel macOS support, app notifications and file/SSH controls, app-owner CLI
// authorization, SSH grants across verified memberships, multi-coordinator joins.
// Previous sync (rayfish@873cd6a..7ef6b2de):
// Desktop apps and installation, delegated machine control and receipts,
// managed apply, DNS controls, file rejection/cancellation, identity backups,
// protocol 6 fragmentation and protocol 5 compatibility, 1500-byte MTU.
// Previous sync:
// (rayfish@90a4c14..873cd6a): WINDOWS GREW AN INSTALLER AND AN ARCHITECTURE.
// Releases publish `ray-windows-aarch64.exe`, so ARM laptops get a native binary,
// and `install.ps1` is the counterpart to install.sh: x64 downloads and runs the
// MSI, ARM64 (which has no MSI) places the exe with the matching arm64 Wintun DLL
// beside it and adds the dir to PATH, leaving `ray install` to register the
// service. Nothing is signed yet and `ray update` does not cover the ARM64 route,
// so it upgrades by re-running the installer. The site serves it at
// /install.ps1 (public/install.ps1, kept byte-identical to the rayfish repo by
// the same CI job install.sh has) and the landing page offers it when the browser
// looks like Windows. `ray status` now draws a saved-but-unregistered network from
// its config in the block a live one gets, marked `connecting…` until an attempt
// has failed and `offline` with the reason after, every peer offline, member count
// a plain total; an older daemon still gets the one-line `inactive` block.
// `ray firewall ssh show` says a node cannot mesh-SSH its own address (self-traffic
// goes over loopback, never enters the TUN, lands on :22 where nothing is bound)
// and points at `ssh localhost`. Android: identity backup and restore through the
// system file picker, same enc1 format `ray pair backup` prints, offered before a
// new install mints a key of its own; an inbound firewall rule can name a peer;
// the app's file domain is excluded from Auto Backup and device-to-device transfer,
// which had been copying the identity key, the device cert and coordinator keys to
// the account's cloud backup. Chapters touched: Getting Started, Introduction, CLI
// Reference, Mesh SSH, Device Pairing, Identity, Local Device Firewall, Network
// Lifecycle, Troubleshooting.
// Previously (rayfish@864fcad..90a4c14):
// THE MESH IS IPv6-ONLY. Mesh IPv4 (`100.64.0.0/10`, FNV-1a, the collision index
// and the coordinator's seat assignment) is gone; a peer's one address is blake3
// of its identity in `200::/7`, derived locally and never on the wire. `.ray`
// answers AAAA + PTR, NODATA for A. Magic DNS is at `200::53` only. The
// `ipv6-only` setting and `ray up --ipv6-only` are gone with the mode they
// selected (a stale `ipv6_only` key is ignored); nothing claims CGNAT space, so
// coexisting with Tailscale needs no configuration. New `v4-bridge` setting (on
// by default): the daemon answers on the mesh address for local IPv4-only
// listeners, TCP < 32768, Linux + macOS. Mesh protocol 2 -> 5, so every node
// upgrades together; one ALPN (`rayfish/mesh/5`) carries every shared network on
// one QUIC connection per peer, replacing the per-network
// `rayfish/net/<v>/<prefix>`; the wire is msgpack array-encoded (~30% off the
// roster), which makes a struct's slot count part of the version contract.
// Exit nodes tunnel IPv6 only (IPv4 leaves directly, said out loud); gateways
// advertise an IPv6 uplink and one without is refused; DNS follows the tunnel;
// another VPN's routes are copied into the tunnel table so it keeps working; the
// catch-all survives a rebuild; status says when a selection is not in effect.
// Windows x64 (MSI, LocalSystem service, named-pipe IPC by SID, Wintun, NRPT;
// gaps: no `firewall ssh`, no completions, unsigned MSI). Commands moved:
// `ray connections` -> `ray connect`, `ray accept|deny` -> `ray requests <net>
// accept|deny`, `ray auto-update` -> `ray config set auto-update` (old spellings
// still work, hidden). A rejected command now exits non-zero. Hostname defaults
// to the machine's own name, not a random noun. NetworkManager is no longer a DNS
// backend (IPv4-only D-Bus); rayfish shares `/etc/resolv.conf` with another VPN
// instead of refusing it, declines non-`.ray` so the other VPN still resolves,
// releases DNS when it leaves, and removes only its own lines on shutdown; the
// working-resolver check runs on both sides of the NM drop-in. Completions cover
// ids from listings and install where zsh actually looks. Metrics bind
// 127.0.0.1; no metrics collector on Android. Firewall walks the IPv6 extension
// chain and refuses fragments (so an over-MTU datagram does not cross the mesh),
// counted as `malformed`. Config writes are transactional, fsynced, and per-field.
// A kicked member gets a network-scoped notice and leaves on its own; an
// incompatible network is registered as `incompatible` rather than vanishing;
// the daemon reports saved-but-inactive networks itself. Coordinators push
// membership changes and dial disconnected members; Android polls every 15min.
// Security: mesh messages refused unless the sender is on that network's roster;
// only a coordinator announces an admission; device certs verified on every path;
// signed records cannot be rolled back; pairing tickets expire in 5min; report
// bundles are private to the requester and reclaim earlier ones; an exit node
// refuses transit into its own attached prefixes. Android 8.0 minimum, file-offer
// notifications, diagnostics actually attach their logs, Chrome resolves `.ray`
// on an IPv4-only Wi-Fi. Documented across every chapter; Introduction,
// Membership, Magic DNS, Configuration, CLI Reference, Exit Nodes, Transport,
// TUN Device, Troubleshooting, Security Model and Getting Started changed most.
// Previously (rayfish@be286eb..864fcad): mesh SSH grew a full session surface (port,
// (rayfish@be286eb..864fcad): mesh SSH grew a full session surface (port,
// dynamic, reverse and unix-socket forwarding, agent forwarding, scp/sftp via the
// host's sftp-server, login(1)/PAM handoff, SSH_CONNECTION, locale env allow-list,
// signals, per-channel session state so ControlMaster works, X11 refused
// explicitly) plus a host-firewall check that names the command to open the port.
// Got its own Guide chapter (08), so 08-24 shifted to 09-25. IPv6-only mode:
// another VPN on 100.64.0.0/10 (Tailscale) makes the daemon run the data plane
// over 200::/7 alone instead of refusing to start; tri-state `ray config set
// ipv6-only on|off|auto` (+ `ray up --ipv6-only`), reported as `ipv6-only on
// (auto)` in `ray status`, claimed on the signed roster so peers withhold our A
// record, Magic DNS moves to 200::53, `ray exit-node use` refused in the mode.
// Magic DNS now declines any non-`.ray` name the roster doesn't hold (so a network
// called `dev` stops swallowing zed.dev) and bare network names are no longer
// registered with the OS; `.ray` misses are answered here, never forwarded. The
// daemon resolves the relay and discovery server through its own resolver list.
// Settings registry: every single-value setting is a typed key, so `ray config
// get|set` covers ssh, mdns, download-dir/user and firewall.enabled/reject/
// default-in too; unknown keys are caught before connecting. `ray -h` is grouped
// into 8 sections and `--json` is per-command (`ray --json status` no longer
// parses). New `ray logs [--since D] [-f]` (streams over IPC, no root),
// `ray mdns scan` + a `nearby` block in `ray status`, `ray connect <lan-id>`,
// dynamic tab completion installed by `ray up` / `ray completions --install`,
// OpenRC + SysV service support. A saved network that fails to restore is retried
// every minute instead of staying dead. Documented in Mesh SSH (new),
// Introduction, Getting Started, Magic DNS, Local Device Firewall, Exit Nodes,
// Configuration, CLI Reference, Network Lifecycle, Statistics, Troubleshooting,
// Glossary, Transport, TUN Device, Peer Discovery, Security Model. Rest of the
// range is the Android app, the GUI, and internal refactors/tests (no doc impact).
// Earlier (rayfish@ec4f668..be286eb): exit nodes
// (`ray exit-node allow|disallow|use|none|status`; internet gateway only, refuses
// LAN/loopback/link-local incl. cloud metadata; local `exit_allow` gate + an
// advertised offer on the signed roster; `exit_node_use` selection; loop
// prevention via fwmark (Linux) / IP_BOUND_IF (macOS); pre-tunnel connections keep
// answering out their own interface; DNS follows the tunnel; PMTU signaling for
// oversize transited packets). Got its own Guide chapter (09), so 09-23 shifted to
// 10-24. On-demand connections (eager connect at startup, idle teardown after
// `idle_timeout_secs`, default 120s; on by default; `ray config set on-demand
// off`) and the resulting three-state `ray status` (active/idle/offline).
// `ray config` now covers the `auto-update` and `on-demand` on/off toggles.
// `ray send <peer> <files...>`: argument order flipped, multiple files, returns
// immediately, queues for an offline peer (survives restart), `ray files cancel
// <id>`; `--json` for `ray files` is now `{pending, queued}`. Invite codes carry a
// 4-byte checksum (older codes still decode; new codes are 4 chars longer and
// unreadable to older peers). `RAYFISH_CONFIG_DIR` works on every platform.
// FreeBSD: config in /usr/local/etc, logs in /var/log. Magic DNS verifies an
// upstream before trusting it and refuses the resolv.conf takeover when none
// answers. Static musl Linux binaries. Config-writing CLI commands now route
// through the daemon. Malformed short-header IPv4 packets are dropped. Documented
// in Exit Nodes (new), Introduction, Getting Started, Magic DNS, File Sharing,
// Configuration, CLI Reference, Membership, Statistics, Troubleshooting, Glossary,
// Transport, Packet Forwarding, Daemon Architecture, Security Model. Rest of the
// range is the Android app, the `ray gui` dashboard redesign, and internal
// refactors/tests (no doc impact).
// Earlier (rayfish@9c1d576..ec4f668): opt-in
// automatic updates (`ray auto-update on|off`, `ray install --auto-update`;
// daemon stable-channel self-update every 6h; settings `auto_update`). Deep
// links (`ray open rayfish://join|pair/...`). Local browser GUI (`ray gui`).
// Firewall global kill switch (`ray firewall on|off`; `disabled` in
// firewall.toml). File auto-accept from own devices (`ray files auto-accept`,
// download-dir/download-user; `--no-auto-accept-files`; settings download_dir/
// download_user; per-network auto_accept_files). Ephemeral members
// (`ray ephemeral <net> <Nh|Nd|Nw|off|show>`, coordinator-only 1h floor;
// ephemeral_ttl_secs). Local aliases (`ray alias <net> set|list|remove`,
// display-only). Device revocation (`ray unpair`, nullifiers in the signed
// record; self-unpair). Minor: `ray leave` scoped to one network; richer
// `ray status` (via/rtt/tx/rx columns, device grouping, incompatible flag) +
// fast reconnect. Documented in Getting Started, File Sharing, Local Device
// Firewall, Device Pairing, Configuration, Membership, Network Lifecycle,
// Security Model. Rest of the range is the daemon/term/dns refactors + an
// Android app (no doc impact).
// Earlier: Mesh SSH (`ray firewall ssh on|off|
// allow|deny|show`), a Tailscale-style embedded SSH server keyed on mesh
// identity (no SSH keys); per-grant `--user` restricts which local unix users a
// peer may log in as (non-root by default, `*` for any incl. root); presents the
// host's OpenSSH ed25519 key; userspace `:22` NAT so host sshd is untouched.
// Ingress anti-spoofing (inbound packet dropped if its source IP isn't the
// sending peer's mesh IP) underpins it. New `ray kick <net> <peer>`: a
// coordinator removes a member from a closed network; pruned from roster +
// disconnected mesh-wide; refused on open networks / against a coordinator /
// self; kicked node can re-request unless its invite/key is revoked. Documented
// in Local Device Firewall, Security Model, Network Lifecycle, Membership
// (rayfish@d2ad28c..9c1d576).
// Earlier: `ray apply` specs support optional
// top-level `aliases:` (name -> a user's identity, expands to all their joined
// devices) and `groups:` (name -> [alias|hostname]) as firewall subjects/peers;
// expanded client-side at apply time so the blob is unchanged; aliases resolve
// for joined members only. New `ray identityof <net> <host>` prints the identity
// string to paste into `aliases:`. Documented in Access Control + Getting
// Started (rayfish@ea0cb61..d2ad28c). The rest of that range is internal
// refactors and a firewall re-queue bugfix (no doc impact).
// Earlier (rayfish@ea0cb61): Magic DNS reworked to TUN interception
// (intercept `.ray` in the TUN read loop, answer in-daemon via magic IP
// 100.100.100.53, forward non-`.ray` upstream; direct-mode resolv.conf takeover
// with inotify re-assert + NM dns=none drop-in). Config is now sharded:
// settings.toml (globals) + networks/<name>.toml (one per network, atomic
// temp+rename), legacy networks.toml auto-migrates. `ray config get/set/unset`
// for relay / discovery-dns / dns-upstreams overrides (presets rayfish/n0).
// Standby control plane: `ray down` keeps peer connections (data plane only),
// `sudo ray start`/`stop` are the fully-offline switch. `ray ping`/`ray netcheck`
// diagnostics. Firewall: `ray firewall reject on|off` (TCP RST/ICMP-unreachable),
// comma-list ports, additive suggestions (no synthesized catch-all deny).
// Richer debug log files (7-file retention), release notes on `ray update`,
// `pending` summary on `ray status`, reliable `ray hostname` propagation
// (rayfish@67227fd..ea0cb61).
// Earlier: mesh protocol version is a leading
// ALPN segment (`rayfish/net/<v>/<prefix>`), so peers on different mesh versions
// share no common ALPN and can't connect (gate enforced by the QUIC handshake,
// no in-band check). `ray join` pre-checks the coordinator's mesh version from
// the signed record's new `m,<v>` line and bails with a precise "run ray update"
// message; other dial paths map an ALPN mismatch to an "incompatible version"
// hint. ICMP conntrack is echo-type-aware: only outbound echo-request opens a
// flow, only inbound echo-reply rides the bypass, so removing `allow in icmp`
// truly denies inbound ping (rayfish@f30c5ea..67227fd).
// Earlier: `ray update` channels: `--nightly`
// (rolling pre-release, up-to-date decided by sha256 not semver), `--version V`
// (pin/downgrade), `--list`; binaries stamped with git short sha so
// `ray version`/`ray report` print `<version> (<sha>)`; `ray status`/--check flag
// a stale (un-restarted) daemon. `ray invite --qr` makes the QR code opt-in.
// (Protocol compatibility was a semver-derived handshake check here, since
// superseded by the versioned ALPN above.) (rayfish@319dee2..f30c5ea).
// Earlier: secure-by-default firewall, direction-
// aware defaults (inbound TCP/UDP deny, inbound ICMP allow via a seeded removable
// `allow in icmp` rule, outbound allow); `ray firewall default allow|deny` sets
// the inbound default only; firewall.toml splits default_action into
// default_inbound/default_outbound; `add` prepends + merges by selector; applies
// to existing installs on upgrade. Self-updater: `ray update [--check] [--force]`
// (SHA-256-verified GitHub release swap + daemon restart), `ray version`/
// `--version` (rayfish@139255b..319dee2).
// Earlier: direct 2-peer connections (`ray connect`
// + rotatable contact id, `ray connections approve`), folded into Membership;
// reusable invite keys (`ray invite --reusable`) for unattended fleets (hash
// rides the signed record, revoke propagates, non-authoritative hostname,
// expiry≠member-expiry). Corrected: multi-coordinator admission (co-coordinators
// admit, full set published, joiner dials with fallback → survives one
// coordinator offline); IPv4 collisions resolved by a per-member collision
// index (lowest-free + deterministic reconverge), not a hard deny. Transport:
// fixed UDP port 41383 (manually forwardable) + auto port-mapping. Minor:
// `ray firewall pending` interactive picker, global --json (rayfish@60c414b..139255b).
// Earlier: the `trusted` network concept was removed.
// Networks are now just open/closed (admission only); firewall suggestions are
// advisory on EVERY network. Consent is per-node, per-network: `ray join
// --auto-accept-firewall` (renamed from `--allow-trusted`) or `ray firewall
// auto-accept <net> on|off` (dropped global `ray up --allow-trusted`). New `*`
// wildcard subject (all hosts) + `*` any-peer key. `ray apply` is YAML-only with
// no file-level `trusted:` flag. Hostname authority follows the invite binding,
// not a network flag. config: `auto_accept_firewall` replaces `trusted`/
// `allow_trusted` (rayfish@3895862..60c414b).
// Earlier: "How It Works" overview chapter; file-level `trusted` apply spec;
// 1Password pair backup/restore; audit logging (rayfish@a75e5d0..3895862).
// Earlier: ACL layer removed, replaced by trusted networks + coordinator-
// suggested firewall rules, multi-admin, ray apply (rayfish@2e3f7a5..b18f916);
// proto-explicit tokens, `default` removed (whitelist/blacklist implicit).
// Diagnostics (ray report, file logging, OTLP, panic capture): rayfish@491ad8c.
// Segmentation-first networking: rayfish@2a1242b.
// See ../../readme.md ("Docs sync") for the update workflow.
// Chapters are grouped into sections in the sidebar. `section` is the group
// label; order within the array is the reading order (prev/next + the llms.txt
// index, which sorts by the NN file prefix, so keep prefix == reading order).
const chapters = [
  // Guide: install it, understand it, use each feature.
  { num: 1, section: "Guide", slug: "01-introduction", title: "Introduction" },
  { num: 2, section: "Guide", slug: "02-getting-started", title: "Getting Started" }, // prettier-ignore
  { num: 3, section: "Guide", slug: "03-how-it-works", title: "How It Works" },
  { num: 4, section: "Guide", slug: "04-magic-dns", title: "Magic DNS" },
  { num: 5, section: "Guide", slug: "05-file-sharing", title: "File Sharing" },
  { num: 6, section: "Guide", slug: "06-access-control", title: "Access Control" }, // prettier-ignore
  { num: 7, section: "Guide", slug: "07-declarative-provisioning", title: "Declarative Provisioning" }, // prettier-ignore
  { num: 8, section: "Guide", slug: "08-local-device-firewall", title: "Local Device Firewall" }, // prettier-ignore
  { num: 9, section: "Guide", slug: "09-mesh-ssh", title: "Mesh SSH" },
  { num: 10, section: "Guide", slug: "10-device-pairing", title: "Device Pairing" }, // prettier-ignore
  { num: 11, section: "Guide", slug: "11-exit-nodes", title: "Exit Nodes" },
  {
    num: 12,
    section: "Guide",
    slug: "12-configuration",
    title: "Configuration",
  },
  // Reference: look-up tables and operational chapters.
  { num: 13, section: "Reference", slug: "13-cli-reference", title: "CLI Reference" }, // prettier-ignore
  { num: 14, section: "Reference", slug: "14-membership", title: "Membership" },
  { num: 15, section: "Reference", slug: "15-network-lifecycle", title: "Network Lifecycle" }, // prettier-ignore
  { num: 16, section: "Reference", slug: "16-statistics", title: "Statistics" },
  { num: 17, section: "Reference", slug: "17-audit-logging", title: "Audit Logging" }, // prettier-ignore
  { num: 18, section: "Reference", slug: "18-troubleshooting", title: "Troubleshooting" }, // prettier-ignore
  { num: 19, section: "Reference", slug: "19-glossary", title: "Glossary" },
  // Internals: how each subsystem works under the hood.
  { num: 20, section: "Internals", slug: "20-identity", title: "Identity" },
  { num: 21, section: "Internals", slug: "21-transport", title: "Transport" },
  { num: 22, section: "Internals", slug: "22-tun-device", title: "TUN Device" },
  { num: 23, section: "Internals", slug: "23-packet-forwarding", title: "Packet Forwarding" }, // prettier-ignore
  { num: 24, section: "Internals", slug: "24-peer-discovery", title: "Peer Discovery" }, // prettier-ignore
  { num: 25, section: "Internals", slug: "25-daemon-architecture", title: "Daemon Architecture" }, // prettier-ignore
  { num: 26, section: "Internals", slug: "26-security-model", title: "Security Model" }, // prettier-ignore
];

// Which `ray` the docs currently describe. Surfaced in the docs sidebar and
// kept in step with readme.md ("Docs sync"). Bump both together.
export const docsSync = { commit: "a2ca33cf", date: "2026-10-12" };

// What DocsView needs to render this book rather than the other one: the
// eyebrow above each chapter, the path its links are built from, and the
// binary the sidebar stamp names. `back` is for a book that hangs off a
// product page; this one is the site's own docs, so it has none.
export const meta = {
  id: "rayfish",
  label: "The Rayfish Book",
  base: "/docs",
  binary: "ray",
  sync: docsSync,
  back: null,
};

export default chapters;
